<script setup>
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import CoinGlyph from '@/design/primitives/CoinGlyph.vue'
import IconExternal from '@/design/icons/IconExternal.vue'

const props = defineProps({
  user: { type: Object, required: true }
})

function formatDate(raw) {
  if (!raw) return '—'
  return new Date(raw).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  })
}

function balance(raw) {
  return (parseFloat(raw) || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  })
}
</script>

<template>
  <section class="dossier-section" aria-labelledby="account-heading">
    <h2 id="account-heading" class="stamp u-display u-allcaps">Account</h2>

    <!-- Two-column identity + metrics -->
    <div class="account-columns">

      <!-- Left: identity card -->
      <PanelOrganic tone="strong" :padded="true" class="identity-card">
        <dl class="identity-grid">
          <div class="identity-cell">
            <dt class="kicker u-display u-allcaps">Display name</dt>
            <dd class="identity-cell__value">{{ user.displayName || '—' }}</dd>
          </div>
          <div class="identity-cell">
            <dt class="kicker u-display u-allcaps">Email</dt>
            <dd class="identity-cell__value identity-cell__value--email">{{ user.email || '—' }}</dd>
          </div>
          <div class="identity-cell">
            <dt class="kicker u-display u-allcaps">Member since</dt>
            <dd class="identity-cell__value u-mono">{{ formatDate(user.createdAt) }}</dd>
          </div>
          <div class="identity-cell">
            <dt class="kicker u-display u-allcaps">Last activity</dt>
            <dd class="identity-cell__value u-mono">{{ formatDate(user.lastActivityAt ?? user.updatedAt) }}</dd>
          </div>
        </dl>

        <!-- Steam badge -->
        <div v-if="user.hasSteam && user.profileUrl" class="steam-badge">
          <svg class="steam-badge__icon" viewBox="0 0 24 24" aria-hidden="true" width="16" height="16">
            <path fill="currentColor" d="M12 0C5.373 0 0 5.373 0 12c0 5.301 3.436 9.8 8.207 11.387.6.111.793-.261.793-.58 0-.286-.01-1.04-.016-2.04-3.338.726-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.09-.745.083-.73.083-.73 1.205.085 1.84 1.237 1.84 1.237 1.07 1.834 2.807 1.304 3.492.997.108-.775.42-1.305.762-1.605-2.665-.303-5.467-1.332-5.467-5.93 0-1.31.468-2.38 1.235-3.22-.124-.303-.535-1.524.117-3.176 0 0 1.008-.323 3.3 1.23A11.5 11.5 0 0 1 12 6.803a11.5 11.5 0 0 1 3.007.404c2.29-1.553 3.296-1.23 3.296-1.23.654 1.652.243 2.873.12 3.176.768.84 1.233 1.91 1.233 3.22 0 4.61-2.807 5.624-5.48 5.921.43.372.814 1.103.814 2.222 0 1.606-.015 2.898-.015 3.293 0 .32.192.695.8.578C20.567 21.795 24 17.298 24 12c0-6.627-5.373-12-12-12"/>
          </svg>
          <a :href="user.profileUrl" target="_blank" rel="noopener noreferrer" class="steam-badge__link">
            STEAM LINKED
            <IconExternal style="width:12px;height:12px;opacity:.7;" />
          </a>
        </div>
        <div v-else-if="!user.hasSteam" class="steam-unlinked kicker u-display u-allcaps">
          Steam not linked
        </div>
      </PanelOrganic>

      <!-- Right: metric grid -->
      <div class="metric-grid">
        <!-- Balance -->
        <PanelOrganic tone="glass" :padded="true" class="metric-panel">
          <p class="metric-panel__label kicker u-display u-allcaps">Balance</p>
          <div class="metric-panel__value metric-panel__value--balance">
            <CoinGlyph :size="22" />
            <span class="u-mono u-gold">{{ balance(user.balance) }}</span>
          </div>
          <p class="metric-panel__sub kicker u-display u-allcaps">EVOGEMS</p>
        </PanelOrganic>

        <!-- Total spent -->
        <PanelOrganic tone="glass" :padded="true" class="metric-panel">
          <p class="metric-panel__label kicker u-display u-allcaps">Total spent</p>
          <p class="metric-panel__value u-mono u-gold">
            {{ balance(user.totalSpent ?? 0) }}
          </p>
        </PanelOrganic>

        <!-- Deliveries this month -->
        <PanelOrganic tone="glass" :padded="true" class="metric-panel">
          <p class="metric-panel__label kicker u-display u-allcaps">Deliveries this month</p>
          <p class="metric-panel__value u-mono u-gold">{{ user.deliveriesThisMonth ?? '—' }}</p>
        </PanelOrganic>

      </div>

    </div>
  </section>
</template>

<style scoped>
.dossier-section {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.stamp {
  font-size: clamp(var(--fs-21), 2.5vw, var(--fs-32));
  color: var(--gold);
  letter-spacing: 0.12em;
  margin: 0;
}

/* Two-column layout — both columns stretch to equal height */
.account-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-6);
  align-items: stretch;
}

/* Identity card fills its grid cell */
.identity-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.identity-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-4) var(--sp-6);
  margin: 0;
}

.identity-cell {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
}

.kicker {
  font-size: var(--fs-12);
  letter-spacing: 0.14em;
  color: var(--ink-soft);
  margin: 0;
}

.identity-cell__value {
  font-size: var(--fs-16);
  color: var(--ink);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.identity-cell__value--email {
  font-size: var(--fs-14);
  color: var(--ink-soft);
}

/* Steam badge */
.steam-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  margin-top: var(--sp-6);
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--r-sm);
  border: 1px solid var(--ash-strong);
  background: var(--bg-deep);
  color: var(--ink-soft);
  font-size: var(--fs-12);
}

.steam-badge__icon { flex-shrink: 0; }

.steam-badge__link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--gold);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  transition: color var(--t-fast) var(--ease-cinema);
}
.steam-badge__link:hover { color: var(--gold-deep); }

.steam-unlinked {
  margin-top: var(--sp-6);
  color: var(--ink-mute);
  font-size: var(--fs-12);
}

/* Metric grid — 2x2 with equal-height rows; fills the right column */
.metric-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 1fr;
  gap: var(--sp-4);
  height: 100%;
}

.metric-panel {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  height: 100%;
  justify-content: center;
}

.metric-panel__label {
  font-size: var(--fs-11, 0.6875rem);
  letter-spacing: 0.14em;
  color: var(--ink-soft);
  margin: 0;
}

.metric-panel__value {
  font-size: var(--fs-26);
  line-height: 1;
  color: var(--gold);
  margin: 0;
}

.metric-panel__value--balance {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-21);
}

.metric-panel__sub {
  font-size: var(--fs-11, 0.6875rem);
  letter-spacing: 0.14em;
  color: var(--ink-mute);
  margin: 0;
}

@media (max-width: 860px) {
  .account-columns {
    grid-template-columns: 1fr;
  }
  .metric-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 580px) {
  .identity-grid {
    grid-template-columns: 1fr;
  }
  .metric-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
